import { useEffect, useState } from "react";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import { Button, SubTitle } from "../../Components/CustomComponents";
import LoadingButton from "../../Components/CustomComponents/LoadingButton";
import Table from "../../Components/Table/Table";
import { API_URL, INITIAL_TOKEN } from "../../config/config";
import { formatedDate } from "../../constants";
import { useGetAllTransactionsQuery } from "../../Data/services/transactionApi";
import { getToken } from "../../Data/services/localStorageService";
import Toast from "../../Hooks/useToast";
import styles from "./styles/index.module.scss";

const INSTANCE_TYPE_LABELS = {
  10: "CUSTOM_REQUEST",
  20: "SUBSCRIPTION",
  30: "TIP",
  CUSTOM_REQUEST: "CUSTOM_REQUEST",
  SUBSCRIPTION: "SUBSCRIPTION",
  TIP: "TIP",
};

const TRANSACTION_TYPE_LABELS = {
  10: "DEBIT",
  20: "CREDIT",
  DEBIT: "DEBIT",
  CREDIT: "CREDIT",
};

const getInstanceTypeLabel = (value) =>
  INSTANCE_TYPE_LABELS[value] ?? value ?? "-";

const getTransactionTypeLabel = (value) =>
  TRANSACTION_TYPE_LABELS[value] ?? value ?? "-";

const INSTANCE_TYPES = [
  { label: "All Instance Types", value: "" },
  { label: "Custom Request", value: 10 },
  { label: "Subscription", value: 20 },
  { label: "Tip", value: 30 },
];

const TRANSACTION_TYPES = [
  { label: "All Types", value: "" },
  { label: "Debit", value: 10 },
  { label: "Credit", value: 20 },
];

const TransactionManagement = () => {
  const [indicationMessage, setIndicationMessage] = useState("loading...");
  const [rowData, setRowData] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [userId, setUserId] = useState("");
  const [instanceType, setInstanceType] = useState("");
  const [type, setType] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const { data, isLoading, isFetching } = useGetAllTransactionsQuery(
    {
      limit: pageSize,
      page: currentPage,
      user_id: userId,
      instance_type: instanceType,
      type,
    },
    { refetchOnMountOrArgChange: true },
  );

  useEffect(() => {
    if (data) {
      setRowData(data?.data || []);
      setTotalRecords(
        data?.links?.total_records ?? data?.links?.total ?? data?.data?.length ?? 0,
      );

      if (data?.links?.per_page) {
        setPageSize(Number(data.links.per_page));
      }

      if (data?.links?.current) {
        setCurrentPage(Number(data.links.current));
      }

      setIndicationMessage(
        data?.data?.length === 0 ? "No Data available" : "loading...",
      );
    }
  }, [data]);

  const buildFilterParams = () => {
    const params = new URLSearchParams();

    if (userId) params.append("user_id", userId);
    if (instanceType !== "") params.append("instance_type", instanceType);
    if (type !== "") params.append("type", type);

    return params.toString();
  };

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const queryString = buildFilterParams();
      const url = `${API_URL}transaction/export${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          authorization: `Bearer ${getToken()}`,
          token: INITIAL_TOKEN,
        },
      });

      if (!response.ok) {
        throw new Error("Export failed");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      Toast("Failed to export transactions", "error");
    } finally {
      setIsExporting(false);
    }
  };

  const handleTableChange = (changeType, { page, sizePerPage }) => {
    if (changeType === "pagination") {
      setCurrentPage(Number(page));
      setPageSize(Number(sizePerPage));
    }
  };

  const columns = [
    {
      dataField: "user",
      text: "User",
      sort: false,
      headerStyle: { width: "180px" },
      formatter: (_, row) => row?.user?.name || "-",
    },
    {
      dataField: "from_user",
      text: "From User",
      sort: false,
      headerStyle: { width: "180px" },
      formatter: (_, row) => row?.from_user?.name || "-",
    },
    {
      dataField: "instance_type",
      text: "Instance Type",
      sort: true,
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "150px" },
      formatter: (_, row) => getInstanceTypeLabel(row?.instance_type),
    },
    {
      dataField: "type",
      text: "Type",
      sort: true,
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "100px" },
      formatter: (_, row) => {
        const label = getTransactionTypeLabel(row?.type);

        if (label === "CREDIT") {
          return <div className={styles.credit}>Credit</div>;
        }

        if (label === "DEBIT") {
          return <div className={styles.debit}>Debit</div>;
        }

        return label;
      },
    },
    {
      dataField: "amount",
      text: "Amount",
      sort: true,
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "100px" },
      formatter: (_, row) => `$${Number(row?.amount ?? 0).toLocaleString()}`,
    },
    {
      dataField: "gateway_transaction_id",
      text: "Gateway ID",
      sort: false,
      headerStyle: { width: "220px" },
      formatter: (_, row) => row?.gateway_transaction_id || "-",
    },
    {
      dataField: "description",
      text: "Description",
      sort: false,
      headerStyle: { width: "200px" },
      formatter: (_, row) => row?.description || "-",
    },
    {
      dataField: "createdAt",
      text: "Created At",
      sort: true,
      headerStyle: { width: "150px" },
      formatter: (_, row) =>
        row?.createdAt ? formatedDate(row.createdAt) : "-",
    },
  ];

  return (
    <div className={styles.MainContainer}>
      <SubTitle text={"Transaction Management"} />

      <div className={styles.toolbar}>
        <input
          type="number"
          placeholder="User ID"
          className={styles.filterInput}
          value={userId}
          onChange={(event) => {
            setUserId(event.target.value);
            setCurrentPage(1);
          }}
        />

        <select
          className={styles.filterInput}
          value={instanceType}
          onChange={(event) => {
            setInstanceType(event.target.value);
            setCurrentPage(1);
          }}
        >
          {INSTANCE_TYPES.map((option) => (
            <option key={option.label} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <select
          className={styles.filterInput}
          value={type}
          onChange={(event) => {
            setType(event.target.value);
            setCurrentPage(1);
          }}
        >
          {TRANSACTION_TYPES.map((option) => (
            <option key={option.label} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <Button
          text={isExporting ? "Exporting..." : "Export"}
          className={styles.exportButton}
          onClick={handleExport}
          disabled={isExporting}
        />
      </div>

      <div className={styles.mainTable}>
        {(isLoading || isFetching) && rowData.length === 0 ? (
          <LoadingButton />
        ) : (
          <Table
            data={rowData}
            columns={columns}
            indication={() => indicationMessage}
            remotePagination
            page={currentPage}
            sizePerPage={pageSize}
            totalSize={totalRecords}
            onTableChange={handleTableChange}
          />
        )}
      </div>
    </div>
  );
};

export default TransactionManagement;
